import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import Brand from './Brand';
import Icon from './Icon';
import LocationPicker from './LocationPicker';
import Sheet from './Sheet';
import Tip from './Tip';
import NotificationsMenu from './NotificationsMenu';
import MessagesSheetContent from './MessagesSheetContent';
import SavedSheetContent from './SavedSheetContent';
import { useSheets } from '../hooks/useSheets';

const NAV = [
  { to: '/', icon: 'house', label: 'Home' },
  { to: '/search', icon: 'search', label: 'Search' },
  { to: '/sell', icon: 'tag', label: 'Sell' },
  { to: '/account', icon: 'circleUser', label: 'Account' },
];

export default function Shell({
  children,
  user,
  browseLocation,
  onRequestAuth,
}) {
  const location = useLocation();
  const nav = useNavigate();
  const [search, setSearch] = useState('');
  const [showLocation, setShowLocation] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [messagesUnread, setMessagesUnread] = useState(0);
  const {
    messagesOpen,
    threadId,
    setThreadId,
    openMessages,
    closeMessages,
    notificationsOpen,
    openNotifications,
    closeNotifications,
    savedOpen,
    closeSaved,
  } = useSheets();

  useEffect(() => {
    if (!user) {
      setNotificationCount(0);
      setMessagesUnread(0);
      return undefined;
    }
    const q = query(
      collection(db, 'notifications', user.uid, 'items'),
      orderBy('createdAt', 'desc'),
    );
    const unsubNotes = onSnapshot(
      q,
      (snap) => {
        setNotificationCount(snap.docs.filter((d) => d.data().read !== true).length);
      },
      () => setNotificationCount(0),
    );
    const convQ = query(
      collection(db, 'conversations'),
      where('participantIds', 'array-contains', user.uid),
      orderBy('updatedAt', 'desc'),
    );
    const unsubMsgs = onSnapshot(
      convQ,
      (snap) => {
        const uid = user.uid;
        let total = 0;
        snap.docs.forEach((d) => {
          const data = d.data();
          if (data.deletedForEveryone) return;
          if (Array.isArray(data.hiddenFor) && data.hiddenFor.includes(uid)) return;
          const count = data.unreadCounts?.[uid] || 0;
          const marked = Array.isArray(data.markedUnreadFor) && data.markedUnreadFor.includes(uid);
          if (count > 0 || marked) total += 1;
        });
        setMessagesUnread(total);
      },
      () => setMessagesUnread(0),
    );
    return () => {
      unsubNotes();
      unsubMsgs();
    };
  }, [user]);

  const goSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    nav(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const activePath = location.pathname;

  const locationLabel = useMemo(() => {
    if (!browseLocation?.location) return 'Set location';
    return browseLocation.label || 'Set location';
  }, [browseLocation?.label, browseLocation?.location]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-inner">
          <Brand className="topbar-brand" />

          <div className="header-center">
            <form className="header-search" onSubmit={goSearch}>
              <Icon name="search" size={18} className="header-search-icon" />
              <input
                aria-label="Search listings"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search listings nearby"
                onFocus={() => {
                  if (!user) onRequestAuth?.();
                }}
              />
              <Tip label="Change location">
                <button
                  type="button"
                  className="header-location"
                  onClick={(e) => {
                    e.preventDefault();
                    if (user) setShowLocation(true);
                    else onRequestAuth?.();
                  }}
                  aria-label={
                    browseLocation?.location
                      ? `Change location, currently ${locationLabel}`
                      : 'Change location'
                  }
                >
                  <Icon name="mapPin" size={14} />
                  <span className="header-location-text">
                    <b>{locationLabel}</b>
                    {browseLocation?.location ? (
                      <small>{browseLocation.radiusMiles} mi</small>
                    ) : null}
                  </span>
                </button>
              </Tip>
            </form>
          </div>

          <div className="header-actions">
            <div className="notifications-anchor">
              <Tip label="Notifications">
                <button
                  type="button"
                  className={`icon-button ${notificationsOpen ? 'is-open' : ''}`}
                  aria-label="Notifications"
                  aria-expanded={notificationsOpen}
                  aria-haspopup="dialog"
                  onClick={() => {
                    if (!user) {
                      onRequestAuth?.();
                      return;
                    }
                    if (notificationsOpen) closeNotifications();
                    else openNotifications();
                  }}
                >
                  <Icon name="bell" />
                  {notificationCount > 0 && <span className="dot" />}
                </button>
              </Tip>
              {user ? (
                <NotificationsMenu
                  user={user}
                  open={notificationsOpen}
                  onClose={closeNotifications}
                  onOpenListing={(listingId) => {
                    closeNotifications();
                    nav(`/listing/${listingId}`);
                  }}
                  onOpenConversation={(conversationId) => {
                    closeNotifications();
                    openMessages(conversationId);
                  }}
                />
              ) : null}
            </div>
            <Tip label="Messages">
              <button
                type="button"
                className="icon-button"
                aria-label="Messages"
                onClick={() => (user ? openMessages() : onRequestAuth?.())}
              >
                <Icon name="message" />
                {messagesUnread > 0 && <span className="dot" />}
              </button>
            </Tip>
            {user ? (
              <Tip label="Account">
                <button
                  type="button"
                  className="avatar-button"
                  onClick={() => nav('/account')}
                  aria-label="Account"
                >
                  {(user.displayName || user.email || 'U')[0].toUpperCase()}
                </button>
              </Tip>
            ) : (
              <button type="button" className="sign-in-link" onClick={() => onRequestAuth?.()}>
                Sign in
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="page">{children}</main>

      <nav className="bottom-nav" aria-label="Primary">
        {NAV.map((item) => {
          const active =
            item.to === '/'
              ? activePath === '/'
              : activePath === item.to || activePath.startsWith(`${item.to}/`);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={active ? 'active' : ''}
              onClick={(e) => {
                if (!user && item.to !== '/') {
                  e.preventDefault();
                  onRequestAuth?.();
                }
              }}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <LocationPicker
        open={showLocation}
        onClose={() => setShowLocation(false)}
        initial={browseLocation?.location}
        radiusMiles={browseLocation?.radiusMiles}
        onConfirm={(place) => {
          browseLocation?.setLocation(place);
          if (place.radiusMiles != null) browseLocation?.setRadiusMiles(place.radiusMiles);
        }}
      />

      <Sheet
        open={messagesOpen}
        onClose={closeMessages}
        title="Messages"
        wide
        flush
        className="messages-panel"
      >
        {user && (
          <MessagesSheetContent
            user={user}
            threadId={threadId}
            onSelectThread={setThreadId}
            onOpenListing={(listingId) => {
              nav(`/listing/${listingId}`);
            }}
          />
        )}
      </Sheet>

      <Sheet
        open={savedOpen}
        onClose={closeSaved}
        title="Saved listings"
        subtitle="Things you’ve bookmarked"
        wide
      >
        {user && (
          <SavedSheetContent
            user={user}
            onOpenListing={(listingId) => {
              nav(`/listing/${listingId}`);
            }}
          />
        )}
      </Sheet>
    </div>
  );
}
